import mixpanel from "mixpanel-browser";

const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN;

/**
 * Initialize Mixpanel tracking
 * Only initializes in non-local environments when token is available
 */
export function initMixpanel() {
  if (process.env.REACT_APP_ENV === "local" || !MIXPANEL_TOKEN) {
    console.log("[Mixpanel] Disabled in local environment or token not set");
    return;
  }

  mixpanel.init(MIXPANEL_TOKEN, {
    debug: process.env.REACT_APP_ENV === "develop",
    track_pageview: "full-url",
    persistence: "localStorage",
    // Enable Session Replay for recording user sessions
    record_sessions_percent: 100, // Record 100% of sessions (adjust as needed for production)
    record_block_selector: ".sensitive-data, [data-sensitive]", // Block sensitive elements from recording
    record_mask_text_selector: "input[type='password'], .mask-text", // Mask sensitive text inputs
  });
}

/**
 * Identify a user for tracking
 * Call this when user logs in or when user data is loaded
 */
export function identifyUser(userId: string, userProperties?: Record<string, unknown>) {
  if (process.env.REACT_APP_ENV === "local" || !MIXPANEL_TOKEN) return;

  mixpanel.identify(userId);
  
  if (userProperties) {
    mixpanel.people.set(userProperties);
  }
}

/**
 * Reset user identity (call on logout)
 */
export function resetUser() {
  if (process.env.REACT_APP_ENV === "local" || !MIXPANEL_TOKEN) return;
  
  mixpanel.reset();
}

/**
 * Track a custom event
 */
export function trackEvent(eventName: string, properties?: Record<string, unknown>) {
  if (process.env.REACT_APP_ENV === "local" || !MIXPANEL_TOKEN) return;

  mixpanel.track(eventName, properties);
}

/**
 * Track page view (useful for SPA route changes)
 */
export function trackPageView(pageName: string, properties?: Record<string, unknown>) {
  if (process.env.REACT_APP_ENV === "local" || !MIXPANEL_TOKEN) return;

  mixpanel.track("Page View", {
    page: pageName,
    url: window.location.pathname,
    ...properties,
  });
}

// Common event names for consistency
export const MixpanelEvents = {
  // Auth events
  LOGIN: "Login",
  LOGOUT: "Logout",
  SIGNUP: "Signup",
  PASSWORD_RESET: "Password Reset",
  
  // Case events
  CASE_ADDED: "Case Added",
  CASE_REMOVED: "Case Removed",
  CASE_VIEWED: "Case Viewed",
  CASE_SEARCHED: "Case Searched",
  
  // Activity events
  ACTIVITY_VIEWED: "Activity Viewed",
  
  // Account events
  PROFILE_UPDATED: "Profile Updated",
  PASSWORD_CHANGED: "Password Changed",
  SETTINGS_CHANGED: "Settings Changed",
  
  // Onboarding
  ONBOARDING_STARTED: "Onboarding Started",
  ONBOARDING_COMPLETED: "Onboarding Completed",
} as const;
